import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, allCategory } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const allCategories = [allCategory, ...categories];
  const currentCategory = allCategories.find((c) => c.slug === activeSlug) || allCategory;
  const categoryArticles = useMemo(() => getArticlesByCategory(activeSlug), [activeSlug]);

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteInfo.fullName}`,
    description: currentCategory.description,
    url: `https://www.renrenchucai.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.fullName,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webpageJsonLd)}</script>

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-50 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              {currentCategory.description}
            </p>
          </div>
        </div>

        <div className="border-b border-background-200 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:translate-y-[-4px] cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full font-medium">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-heart-line"></i>
                            {article.likes >= 1000 ? `${(article.likes / 1000).toFixed(1)}k` : article.likes}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}